import { useEffect, useRef, useState } from 'react';
import { Pin } from 'lucide-react';
import { useCanvasStore } from '../store/canvas-store';
import { ZOOM_MIN, ZOOM_MAX } from '../store/commands';
import { boundsOf, selectedFrames } from '../store/selectors';
import { runChatTurn } from '../agent/chatTurn';
import { decideChatTarget } from './chatTarget';
import { ensurePanelChat } from './panel/panelFrames';
import { usePanelStore } from './panel/panel-store';
import { currentReadOnly } from './readonly';

/** Approx bubble height reserved below the framed selection (px). */
const BUBBLE_RESERVE = 200;
const FRAME_GAP = 24;
const FIT_PAD = 60;

interface BubbleState {
  open: boolean;
  x: number;
  y: number;
  /** Auto-anchor at trigger time. */
  anchorId?: string;
  /** Once user accepts (submit / pin), we have a real ChatFrame id. */
  pendingFrameId?: string;
  /** When 'theme', the submitted text is wrapped into a theme-generation
   *  prompt and the bubble shows theme-flavored label/placeholder. */
  intent?: 'theme';
  /** Pre-fill the textarea (e.g. "Add a Lottie animation of "). */
  prefill?: string;
}

/** Grow a textarea to fit its content, up to a max height (then scroll). */
const MAX_BUBBLE_TEXTAREA_PX = 240;
function autoGrow(el: HTMLTextAreaElement | null): void {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, MAX_BUBBLE_TEXTAREA_PX)}px`;
}

/**
 * Global ⌘K chat launcher. Press ⌘K (or Ctrl-K) anywhere → a large chat bubble
 * opens. With a frame selected, the selection is zoomed to fit the upper region
 * and the bubble is centered just below it (both visible); otherwise the bubble
 * is viewport-centered and the chat is global-scoped.
 *
 * Submit spawns an on-canvas chat frame, kicks off the agent turn, and dismisses
 * the launcher — the canvas frame owns the conversation. Pin keeps it without a
 * prompt. Esc dismisses.
 */
export function CmdK() {
  const [state, setState] = useState<BubbleState>({ open: false, x: 0, y: 0 });
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const dispatch = useCanvasStore((s) => s.dispatch);
  const setPanelOpen = usePanelStore((s) => s.setOpen);
  const setChatGlobalOverride = usePanelStore((s) => s.setChatGlobalOverride);

  // ⌘K / Ctrl-K global handler.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (currentReadOnly()) return; // viewers can't spawn an agent/edit bubble
        const st = useCanvasStore.getState();
        const sel = st.selection[0];
        const winW = window.innerWidth;
        const winH = window.innerHeight;
        let x = winW / 2;
        let y = winH / 2;
        const b = boundsOf(selectedFrames(st));
        if (b) {
          // Zoom the selection to fit the upper region, reserving room below
          // for the bubble — frame + chat both visible, bubble centered under it.
          const availW = Math.max(winW - FIT_PAD * 2, 1);
          const availH = Math.max(winH - FIT_PAD * 2 - BUBBLE_RESERVE - FRAME_GAP, 1);
          const zoom = Math.min(Math.max(Math.min(availW / b.w, availH / b.h), ZOOM_MIN), ZOOM_MAX);
          const topRegionH = winH - BUBBLE_RESERVE - FRAME_GAP;
          const cx = b.x + b.w / 2;
          const cy = b.y + b.h / 2;
          const pan = { x: winW / 2 - cx * zoom, y: topRegionH / 2 - cy * zoom };
          st.dispatch({ t: 'viewport.set', pan, zoom });
          // Bubble sits centered horizontally, just below the framed selection.
          x = winW / 2;
          y = pan.y + (b.y + b.h) * zoom + FRAME_GAP;
        }
        setState({ open: true, x, y, anchorId: sel });
      } else if (e.key === 'Escape' && state.open) {
        dismiss();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.open]);

  // Programmatic open for theme generation — viewport-centered, global scope,
  // theme intent. Dispatched by the ✨ Generate command in the ⌘P palette.
  useEffect(() => {
    const onTheme = () =>
      setState({ open: true, x: window.innerWidth / 2, y: window.innerHeight / 2, intent: 'theme' });
    window.addEventListener('codeframe:generate-theme', onTheme);
    return () => window.removeEventListener('codeframe:generate-theme', onTheme);
  }, []);

  // Programmatic open with a pre-filled draft — viewport-centered, global scope.
  // Dispatched by ⌘P entries (e.g. "Add Lottie animation…").
  useEffect(() => {
    const onPrefill = (e: Event) => {
      const prefill = (e as CustomEvent<string>).detail ?? '';
      setState({ open: true, x: window.innerWidth / 2, y: window.innerHeight / 2, prefill });
    };
    window.addEventListener('codeframe:prefill-chat', onPrefill);
    return () => window.removeEventListener('codeframe:prefill-chat', onPrefill);
  }, []);

  // Focus + size textarea on open. When opened with a prefill, seed the textarea
  // and place the caret at the end so the user types straight into the prompt.
  useEffect(() => {
    if (state.open) {
      const el = inputRef.current;
      if (el && state.prefill) {
        el.value = state.prefill;
        const end = el.value.length;
        el.setSelectionRange(end, end);
      }
      el?.focus();
      autoGrow(el);
    }
  }, [state.open, state.prefill]);

  const dismiss = () => setState((s) => ({ ...s, open: false, pendingFrameId: undefined }));

  /** Resolve the panel-owned chat to run in: theme + global intents → global;
   *  otherwise follow the current selection. Opens the panel + sets scope. */
  const routeToPanelChat = (): string => {
    const st = useCanvasStore.getState();
    const target =
      state.intent === 'theme'
        ? ({ scope: 'global' } as const)
        : decideChatTarget(st.selection, st.doc.frames);
    const id = ensurePanelChat({
      frames: st.doc.frames,
      dispatch,
      anchorId: target.scope === 'frame' ? target.anchorId : undefined,
    });
    setChatGlobalOverride(target.scope === 'global');
    setPanelOpen(true);
    return id;
  };

  const onSubmit = () => {
    const raw = inputRef.current?.value.trim() ?? '';
    if (!raw) return;
    const text =
      state.intent === 'theme'
        ? `Generate a design theme and apply it as the active theme: ${raw}`
        : raw;
    const id = routeToPanelChat();
    if (inputRef.current) inputRef.current.value = '';
    // runChatTurn manages its own transaction. The panel conversation owns it.
    void runChatTurn(id, text);
    // Dismiss the launcher.
    setState((s) => ({ ...s, open: false, pendingFrameId: undefined }));
  };

  const onPin = () => {
    routeToPanelChat();
    setState((s) => ({ ...s, open: false }));
  };

  if (!state.open) return null;

  return (
    <>
      {/* Outside-click dismiss layer */}
      <div onClick={dismiss} className="fixed inset-0 z-[99]" />
      <div
        data-anim
        className="cf-pop-in fixed z-[100] w-[min(560px,90vw)] p-4 rounded-[12px] font-mono text-sm bg-[var(--color-menu-bg)] border border-[var(--color-tool-code)] text-[var(--color-text)] shadow-[var(--shadow-2)]"
        style={{
          left: state.x,
          top: state.y,
          // Frame selected → anchor below it (top-aligned). Global → viewport-centered.
          transform: state.anchorId ? 'translate(-50%, 0)' : 'translate(-50%, -50%)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-2.5 text-xs">
          <span
            className={
              'font-semibold ' +
              (state.intent === 'theme' ? 'text-[var(--color-tool-fx)]' : 'text-[var(--color-tool-chat)]')
            }
          >
            {state.intent === 'theme' ? 'Generate theme' : 'Ask Viv'}
          </span>
          <span className="text-[var(--color-text-muted)]">
            {state.intent === 'theme' ? 'new active theme' : state.anchorId ? `↳ ${state.anchorId}` : 'whole canvas'}
          </span>
        </div>
        <textarea
          ref={inputRef}
          rows={2}
          placeholder={
            state.intent === 'theme'
              ? 'Describe the theme — vibe, colors, type…'
              : state.anchorId
                ? 'Tell Viv how to grow this frame…'
                : 'Tell Viv what to grow on the canvas…'
          }
          onInput={(e) => autoGrow(e.currentTarget)}
          className="w-full p-3 rounded-[8px] font-sans text-[15px] leading-relaxed resize-none overflow-y-auto outline-none max-h-[240px] bg-[var(--color-input-bg)] border border-[var(--color-input-border)] text-[var(--color-text)]"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSubmit();
            }
          }}
        />
        <div className="flex justify-between items-center mt-2.5 text-xs text-[var(--color-text-muted)]">
          <span>⏎ send · esc cancel</span>
          <button
            onClick={onPin}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-[6px] border-0 text-xs font-semibold cursor-pointer bg-[var(--color-tool-fx)] text-black"
          >
            <Pin size={13} /> pin
          </button>
        </div>
      </div>
    </>
  );
}
